/**
 * The connection to the server that introduces this session to the account's others.
 *
 * Held open for as long as somebody is signed in, because presence is the whole of what it
 * provides: how many other sessions are there to share with, and how to reach them. It
 * carries no models and never sees one.
 *
 * Reconnects on its own with a widening delay. A session that quietly stopped being
 * reachable would show a share button that did nothing, which is worse than a button that
 * says nobody is there.
 */

import { logger } from '../log.js';

const log = logger('share');

export interface PeerView {
  readonly peer: string;
  readonly install: string;
}

/** What `RTCPeerConnection` needs to find a route, as the server supplies it. */
export interface IceConfig {
  readonly urls: readonly string[];
  readonly username?: string | undefined;
  readonly credential?: string | undefined;
}

export interface SignallingHandlers {
  /** Connected: this session's name, who else is present, and how to reach them. */
  readonly onReady: (self: string, peers: readonly PeerView[], ice: IceConfig) => void;
  readonly onJoined: (peer: PeerView) => void;
  readonly onLeft: (peer: string) => void;
  readonly onSignal: (from: string, payload: unknown) => void;
  /** Disconnected, for whatever reason. Nobody is reachable until `onReady` again. */
  readonly onDown: (reason: string | undefined) => void;
}

const FIRST_RETRY_MS = 1000;
const MAX_RETRY_MS = 30_000;

export class Signalling {
  private socket: WebSocket | undefined;
  private retryMs = FIRST_RETRY_MS;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private wanted = false;

  constructor(
    private readonly apiBaseUrl: string,
    private readonly installId: string,
    /** Read at connection time, because a reconnection after a re-login needs the new one. */
    private readonly csrfToken: () => string | undefined,
    private readonly handlers: SignallingHandlers,
  ) {}

  start(): void {
    this.wanted = true;
    this.open();
  }

  stop(): void {
    this.wanted = false;
    if (this.retryTimer !== undefined) clearTimeout(this.retryTimer);
    this.retryTimer = undefined;

    const socket = this.socket;
    this.socket = undefined;
    socket?.close();
  }

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /** Pass connection detail to another session. Silently dropped if not connected. */
  send(to: string, payload: unknown): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify({ type: 'signal', to, payload }));
  }

  private open(): void {
    const csrf = this.csrfToken();
    if (csrf === undefined) {
      // No token means no live session, so there is nothing to connect as. Retrying
      // handles the ordinary case of this running a moment before the session is known.
      this.retryLater('not signed in');
      return;
    }

    let socket: WebSocket;
    try {
      socket = new WebSocket(signallingUrl(this.apiBaseUrl));
    } catch (error) {
      log.warn(`Could not open a sharing connection to ${this.apiBaseUrl}.`, error);
      this.retryLater('could not connect');
      return;
    }

    this.socket = socket;

    socket.addEventListener('open', () => {
      socket.send(JSON.stringify({ type: 'hello', csrf, install: this.installId }));
    });

    socket.addEventListener('message', (event: MessageEvent<unknown>) => {
      if (typeof event.data === 'string') this.receive(event.data);
    });

    socket.addEventListener('close', (event) => {
      // A socket we already let go of in `stop()`, closing as asked. Nothing to report.
      if (this.socket !== socket) return;
      this.socket = undefined;

      // Anything but a clean close is a fact worth having: 1006 in particular is the
      // browser saying the connection vanished without a closing handshake, which points
      // at the network or something in between rather than at the server's own decision.
      const description = describeSocketClose(event);
      if (event.code === CLOSE_NORMAL || event.code === CLOSE_GOING_AWAY) {
        log.info(description);
      } else {
        log.warn(description);
      }

      this.handlers.onDown(undefined);
      this.retryLater('the connection closed');
    });

    // A failure is always followed by a close, which is where the retry is arranged.
    socket.addEventListener('error', () => undefined);
  }

  private receive(text: string): void {
    let message: unknown;
    try {
      message = JSON.parse(text);
    } catch {
      return;
    }
    if (typeof message !== 'object' || message === null) return;

    const body = message as Record<string, unknown>;
    switch (body.type) {
      case 'welcome': {
        // A working connection: start the backoff over, so a later blip retries promptly.
        this.retryMs = FIRST_RETRY_MS;

        const self = asString(body.peer) ?? '';
        const peers = readPeers(body.peers);

        // Where "sharing says nobody is there" is answered. A count of zero here is the
        // difference between a broken connection and a correct one with nothing to talk
        // to, and the interface shows the same thing for both.
        log.info(
          `Signalling connected to ${this.apiBaseUrl} as session ${self}; ` +
            `${describeCount(peers.length)} of this account also connected.`,
        );

        this.handlers.onReady(self, peers, readIce(body.ice));
        return;
      }
      case 'joined': {
        const peer = readPeer(body.peer);
        if (peer) this.handlers.onJoined(peer);
        return;
      }
      case 'left': {
        const peer = asString(body.peer);
        if (peer !== undefined) this.handlers.onLeft(peer);
        return;
      }
      case 'signal': {
        const from = asString(body.from);
        if (from !== undefined) this.handlers.onSignal(from, body.payload);
        return;
      }
      case 'refused': {
        // Turned away rather than disconnected: a reason worth surfacing, and not worth
        // retrying quickly, since retrying will be refused too.
        const reason = asString(body.reason);
        log.warn(
          `Sharing is unavailable: ${reason ?? 'the server refused the connection'}`,
        );
        this.handlers.onDown(reason);
        return;
      }
      default:
        return;
    }
  }

  private retryLater(reason: string): void {
    if (!this.wanted || this.retryTimer !== undefined) return;

    const delay = this.retryMs;
    this.retryMs = Math.min(this.retryMs * 2, MAX_RETRY_MS);

    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      if (this.wanted) this.open();
    }, delay);

    log.debug(`Sharing connection retrying in ${(delay / 1000).toString()}s: ${reason}`);
  }
}

/** A clean close, as either end asking politely produces. */
const CLOSE_NORMAL = 1000;
/** The server shutting down or the page going away. Also unremarkable. */
const CLOSE_GOING_AWAY = 1001;
/** No closing handshake happened at all. */
const CLOSE_ABNORMAL = 1006;

/**
 * Why the socket went away.
 *
 * The close code is the only thing separating "the server sent us away" from "the network
 * went out from under us", and those want quite different investigations. Discarding it —
 * which is what happens if the event is not read — leaves both looking like a share
 * button that stopped working.
 */
function describeSocketClose(event: CloseEvent): string {
  const reason = event.reason === '' ? 'no reason given' : event.reason;
  const hint =
    event.code === CLOSE_ABNORMAL
      ? ' There was no closing handshake, which usually means the network or something ' +
        'between here and the server dropped the connection rather than the server ' +
        'deciding to end it.'
      : '';

  return `The signalling connection closed: code ${event.code.toString()}, ${reason}.${hint}`;
}

function describeCount(others: number): string {
  return others === 1 ? '1 other session' : `${others.toString()} other sessions`;
}

/**
 * Where the signalling socket lives, derived from wherever the API is.
 *
 * Exported so the derivation can be tested: getting it wrong produces a connection that
 * fails in a way indistinguishable from the server being down.
 */
export function signallingUrl(apiBaseUrl: string): string {
  const url = new URL('/api/share/signal', apiBaseUrl);
  url.protocol = url.protocol === 'http:' ? 'ws:' : 'wss:';
  return url.toString();
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function readPeer(value: unknown): PeerView | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const body = value as Record<string, unknown>;
  const peer = asString(body.peer);
  const install = asString(body.install);
  return peer === undefined || install === undefined ? undefined : { peer, install };
}

function readPeers(value: unknown): PeerView[] {
  if (!Array.isArray(value)) return [];
  const peers: PeerView[] = [];
  for (const item of value) {
    const peer = readPeer(item);
    if (peer) peers.push(peer);
  }
  return peers;
}

function readIce(value: unknown): IceConfig {
  if (typeof value !== 'object' || value === null) return { urls: [] };
  const body = value as Record<string, unknown>;
  const urls = Array.isArray(body.urls)
    ? body.urls.filter((url): url is string => typeof url === 'string')
    : [];

  const username = asString(body.username);
  const credential = asString(body.credential);

  return {
    urls,
    ...(username === undefined ? {} : { username }),
    ...(credential === undefined ? {} : { credential }),
  };
}
